import React, { useEffect, useState } from "react";
import { PageContainer } from '@amzn/stencil-react-components/page';
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Label, P } from "@amzn/stencil-react-components/text";
import { Input, Select, TextArea } from "@amzn/stencil-react-components/form";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { getEpochFromTime, getEventModels, getTimeFromEpoch } from "src/service/service";
import {APICallStatus, EventModelStatus, Stage} from "src/enum/enums";
import { Redirect } from "react-router-dom";
import { PathNames, StringUtils, TimestampInputValidity } from "src/constant/constants";
import "src/components/commonStyles.scss";
import { TriggerSelector } from "../authoringComponents";
import * as solutionConfig from "src/config/solutionConfigs.json";
import { useAppDispatch, useAppSelector } from "src/state/hooks";
import { updateEventSolutionType } from "src/state/slice/EventModelSlice";
import { SortDirection, Table, useSort } from "@amzn/stencil-react-components/table";
import { Pagination } from "@amzn/stencil-react-components/pagination";
import { isBefore, PopupDatePicker } from "@amzn/stencil-react-components/date-picker";
import "src/components/authoringComponents/styles/SelectEventModelPage.scss";

const STRINGS_DISPLAYED = {
  search_event_label: "Search on",
  valid_between_label: "Valid between",
  filter_label: "Filter on",
  next_button: "Next",
  error_fetching_event_models: "Error fetching your event models. Please try again later.",
  solution_type_label: "Solution type",
  solution_type_placeholder: "Choose a solution type",
  display_event: "Display Events",
  choose_filter: "Choose a filter",
  enter_search_value: "Enter search value",
  choose_solution_attribute: "Choose a solution attribute",
  note: "* Select trigger and solution type to access more advanced filters",
  edit: "Edit",
  event_name: "eventName",
  event_rule: "eventRule",
  created_by: "createdBy",
  updated_by: "updatedBy"
}

/**
 * Renders the page to let users select existing event models.
 */
export const SelectEventModelPage = () => {
  const [eventModels, setEventModels] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [searchedValue, setSearchedValue] = useState("");
  const [searchAttribute, setSearchAttribute] = useState(null);
  const [searchSolutionAttribute, setSearchSolutionAttribute] = useState(null);
  const [searchedSolutionValue, setSearchedSolutionValue] = useState("");
  const [searchSolutionAttributes, setSearchSolutionAttributes] = useState([]);
  const [triggersAndSolutionTypes, setTriggersAndSolutionTypes] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState("");
  const [validTill, setValidTill] = useState("");

  const searchAttributes = [
    STRINGS_DISPLAYED.created_by,
    STRINGS_DISPLAYED.updated_by,
    STRINGS_DISPLAYED.event_name,
    STRINGS_DISPLAYED.event_rule
  ]

  const ButtonCell = ({ data }) => (
    <Button onClick={() => onNextClick(data)} variant={ButtonVariant.Tertiary}> {STRINGS_DISPLAYED.edit} </Button>
  );

  const [ columnData , setColumnData] = useState<any[]>([]);

  const columns = [
    { header: 'Edit Event', accessor: ({ data }) => data?.eventName, cellComponent: ButtonCell, width: "5%"},
    { header: 'Event Name', accessor: 'eventName', sortId: 'eventName', width: "5%" },
    { header: 'Trigger', accessor: 'clientUseCase', sortId: 'clientUseCase', width: '5%' },
    { header: 'Solution Type', accessor: ({ data }) => data.eventSolution.type, width: "5%"},
    { header: 'Created At', accessor: ({ data }) => getTimeFromEpoch(data?.createdAt), sortId: 'createdAt', width: "5%"},
    { header: 'Created By', accessor: 'createdBy', sortId: 'createdBy', width: "5%" },
    { header: 'Updated At', accessor: ({ data }) => getTimeFromEpoch(data?.updatedAt), sortId: 'updatedAt', width: "5%" },
    { header: 'Updated By', accessor: 'updatedBy', sortId: 'updatedBy', width: "5%" },
    { header: 'Valid From', accessor: ({ data }) => getTimeFromEpoch(data?.validFrom), sortId: 'validFrom', width: "5%"},
    { header: 'Valid Till', accessor: ({ data }) => getTimeFromEpoch(data?.validTill), sortId: 'validTill', width: "5%"},
    {
      header: 'Event Rule',
      accessor: ({ data }) => processPayload(data?.eventRule),
      width: "20%"
    },
    { header: 'Event Solution', accessor: ({ data }) => processPayload(JSON.stringify(data.eventSolution)), width: "40%" },
  ];

  const [getEventModelsStatus, setGetEventModelsStatus] =
    useState<APICallStatus>(APICallStatus.SUCCESSFUL);
  const availableSolutions = Object.keys(solutionConfig).filter(solutionType => triggersAndSolutionTypes.includes(solutionType));
  const eventModelState = useAppSelector(({eventModel}) => eventModel);

  const dispatch = useAppDispatch();

  const [redirectToUrl, setRedirectToUrl] = useState<string>("");

  const { activeSortId, sortDirection, onSort } = useSort({
    defaultSortDirection: SortDirection.Ascending,
    defaultActiveSortId: STRINGS_DISPLAYED.event_name
  });

  const compare = (a, b) => {

    const dataA = a[activeSortId];
    const dataB = b[activeSortId];

    return dataA < dataB ? -1 : 1;
  };

  const processPayload = (data) => {
    return (
      <TextArea resize={"both"} width={"100%"} disabled value={data}/>
    )
  }

  const sortedData =
    sortDirection === SortDirection.Descending
      ? columnData?.sort(compare).reverse()
      : columnData?.sort(compare);

  const onNextClick = (eventName : string) => {
    setRedirectToUrl(`${PathNames.MANAGE_EVENT_MODELS_PAGE}/${eventName}`);
  }

  const onSolutionTypeChange = (solutionType: string) => {
    dispatch(updateEventSolutionType(solutionType));
  }

  // To reset column and solution filters to default on Trigger/Solution type selection
  const resetStateVariables = () => {
    setSearchSolutionAttribute(null);
    setSearchAttribute(null);
    setSearchedValue("");
    setSearchedSolutionValue("");
    setValidFrom("");
    setValidTill("");
  }

  useEffect(() => {
    if(eventModelState?.clientUseCase != "") {
      resetStateVariables();
      setColumnData(filterOnTriggerAndSolutionType());
    }

  }, [eventModelState?.clientUseCase])

  useEffect(() => {
    if(eventModelState?.eventSolution?.type) {
      resetStateVariables();
      setSearchSolutionAttributes(Object.keys(eventModelState?.eventSolution?.solutionDetailObject))
      setColumnData(filterOnTriggerAndSolutionType());
    }
  }, [eventModelState?.eventSolution?.type])

  // This generic function filters column data when trigger/solution type is selected
  const filterOnTriggerAndSolutionType = () => {
    const filteredData  = [];
    if(!eventModelState?.clientUseCase){
      return eventModels;
    }
    eventModels.forEach((model) => {
      if(model?.clientUseCase === eventModelState?.clientUseCase && model?.status === EventModelStatus.DRAFT) {
        if(eventModelState?.eventSolution?.type == "") {
          filteredData.push(model);
        }
        else if(eventModelState?.eventSolution?.type == model?.eventSolution?.type) {
          filteredData.push(model);
        }
      }
    })
    return filteredData;
  }

  // Search column data based on selected value in filters
  const searchSelectedValue = () => {
    const currentData = filterOnTriggerAndSolutionType();
    const filteredData  = [];

    if(searchedValue != "" && searchedSolutionValue == "") {
      currentData.forEach((model) => {
        if(model[searchAttribute] && model[searchAttribute].includes(searchedValue)) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else if(searchedSolutionValue != '' && searchedValue == '') {
      currentData.forEach((model) => {
        if(model?.eventSolution?.solutionDetail[searchSolutionAttribute]?.includes(searchedSolutionValue)) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else if (searchedSolutionValue != '' && searchedValue != '') {
      currentData.forEach((model) => {
        if(model[searchAttribute] && model[searchAttribute].includes(searchedValue) &&
        model?.eventSolution?.solutionDetail[searchSolutionAttribute]?.includes(searchedSolutionValue)) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else {
      setColumnData(currentData);
      return currentData;
    }
    return filteredData;
  }

  const filterOnValidDates = () => {
    const currentData = searchSelectedValue();
    const filteredData  = [];

    if(validFrom != StringUtils.EMPTY && validTill == StringUtils.EMPTY) {
      currentData.forEach((model) => {
        if(model[TimestampInputValidity.VALID_FROM] && model[TimestampInputValidity.VALID_FROM] >= getEpochFromTime(new Date(validFrom).toUTCString())) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else if(validFrom == StringUtils.EMPTY && validTill != StringUtils.EMPTY) {
      currentData.forEach((model) => {
        if(model[TimestampInputValidity.VALID_TILL] && model[TimestampInputValidity.VALID_TILL] <= getEpochFromTime(new Date(validTill).toUTCString())) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else if (validFrom != StringUtils.EMPTY && validTill != StringUtils.EMPTY) {
      currentData.forEach((model) => {
        if(model[TimestampInputValidity.VALID_FROM] && model[TimestampInputValidity.VALID_FROM] >= getEpochFromTime(new Date(validFrom).toUTCString()) &&
            model[TimestampInputValidity.VALID_TILL] && model[TimestampInputValidity.VALID_TILL] <= getEpochFromTime(new Date(validTill).toUTCString())) {
          filteredData.push(model);
        }
      })
      setColumnData(filteredData);
    }
    else {
      setColumnData(currentData);
    }
  }

  useEffect(() => {
    setGetEventModelsStatus(APICallStatus.IN_PROGRESS);
    getEventModels(Stage.beta)
      .then((eventModels) => {
        setEventModels(eventModels);
        setColumnData(eventModels);
        setGetEventModelsStatus(APICallStatus.SUCCESSFUL);
      })
      .catch((_) => {
        setGetEventModelsStatus(APICallStatus.FAILED);
      });
  }, []);

  useEffect(() => {
    const triggersAndSolutionTypes = [];
    eventModels.forEach((model) => {
      triggersAndSolutionTypes.push(model?.clientUseCase);
      triggersAndSolutionTypes.push(model?.eventSolution?.type);
    })
    setTriggersAndSolutionTypes(triggersAndSolutionTypes);
  }, [eventModels]);

  return redirectToUrl
    ? <Redirect
      push={true}
      to={{
        pathname: redirectToUrl,
      }}
    />
    : <PageContainer backgroundColor={"white"}>
        {getEventModelsStatus === APICallStatus.IN_PROGRESS
          ? <Spinner />
          : getEventModelsStatus === APICallStatus.FAILED
            ? <MessageBanner type={MessageBannerType.Error}>
              {STRINGS_DISPLAYED.error_fetching_event_models}
            </MessageBanner>
            : <Col gridGap={"24px"}>
                <Row gridGap={"2%"}>
                  <TriggerSelector triggersAndSolutionTypes = {triggersAndSolutionTypes}/>
                  <Label
                    fontSize={"18px"}
                    fontWeight={"bold"}
                    htmlFor={"solution-type"}
                    className={"width_200_px"}
                  >
                    {STRINGS_DISPLAYED.solution_type_label}
                  </Label>
                  <Select
                    id={"solution-type"}
                    options={availableSolutions}
                    value={eventModelState?.eventSolution?.type}
                    onChange={onSolutionTypeChange}
                    disabled={!eventModelState?.clientUseCase}
                    width={"400px"}
                    placeholder={STRINGS_DISPLAYED.solution_type_placeholder}
                  />
                </Row>
                <Row>
                  <P>{STRINGS_DISPLAYED.note}</P>
                </Row>

                <Row gridGap={"2%"}>
                  <Label fontSize={"20px"} fontWeight={"bold"} htmlFor={"event-name-selector"}>
                    {STRINGS_DISPLAYED.search_event_label}
                  </Label>
                  <Select
                    id={"search-attribute"}
                    options={searchAttributes}
                    value={searchAttribute}
                    onChange={(value) => {setSearchAttribute(value)}}
                    width={"250px"}
                    placeholder={STRINGS_DISPLAYED.choose_filter}
                  />
                  <Input
                    onChange={(e) => setSearchedValue(e.target.value)}
                    placeholder={STRINGS_DISPLAYED.enter_search_value}
                    width={"400px"}
                    value={searchedValue}
                    disabled={!searchAttribute}
                    insetElementTrailing={{
                      element: (
                        <Button
                          onClick={() => searchSelectedValue()}
                          size={ButtonSize.Small}>
                          Search
                        </Button>
                      ),
                      inset: 2
                    }}
                    id={"event-name-input"}
                  />
                </Row>
                {eventModelState?.eventSolution?.type &&
                <Row gridGap={"2%"}>
                  <Label fontSize={"20px"} fontWeight={"bold"} htmlFor={"event-name-selector"}>
                    {STRINGS_DISPLAYED.filter_label}
                  </Label>
                  <Select
                    id={"search-attribute"}
                    options={searchSolutionAttributes}
                    value={searchSolutionAttribute}
                    onChange={(value) => {setSearchSolutionAttribute(value)}}
                    width={"250px"}
                    placeholder={STRINGS_DISPLAYED.choose_solution_attribute}
                  />
                  <Input
                    onChange={(e) => setSearchedSolutionValue(e.target.value)}
                    placeholder={STRINGS_DISPLAYED.enter_search_value}
                    width={"400px"}
                    value={searchedSolutionValue}
                    disabled={!searchSolutionAttribute}
                    insetElementTrailing={{
                      element: (
                        <Button
                          onClick={() => searchSelectedValue()}
                          size={ButtonSize.Small}>
                          Search
                        </Button>
                      ),
                      inset: 2
                    }}
                    id={"solution-attribute-input"}
                  />
                </Row>}
                <Row gridGap={"2%"}>
                  <Label fontSize={"20px"} fontWeight={"bold"} htmlFor={"event-name-selector"}>
                    {STRINGS_DISPLAYED.valid_between_label}
                  </Label>
                  <PopupDatePicker
                      id="valid-from-datepicker"
                      labelText="Valid From"
                      showTime={true}
                      onChange={(date) => {
                        setValidFrom(date);
                      }}
                      shouldCloseOnDateSelection={true}
                  />
                  <PopupDatePicker
                      id="valid-till-datepicker"
                      labelText="Valid Till"
                      showTime={true}
                      onChange={(date) => {
                        setValidTill(date);
                      }}
                      shouldCloseOnDateSelection={true}
                      isDateDisabled={(date) => !!date && isBefore(validFrom)(date)}
                  />
                  <Button className={"date-picker-search-button"}
                          onClick={() => {
                            setSelectedPage(1);
                            filterOnValidDates();
                          }}
                          size={ButtonSize.Small}>
                    Search
                  </Button>
                </Row>
                <Table
                  aria-labelledby="event-table"
                  activeSortId={activeSortId}
                  sortDirection={sortDirection}
                  onSort={onSort}
                  columns={columns}
                  data={sortedData?.slice((selectedPage-1)*5, selectedPage*5)}
                  hasStickyHeader={true}
                  maxHeight={"600px"}
                />
                <Spacer height={"24px"}></Spacer>
                <Row alignSelf="center">
                  <Pagination
                    numberOfPages={Math.ceil(sortedData?.length/5)}
                    selectedPage={selectedPage}
                    onPageSelect={setSelectedPage}
                  />
                </Row>
              </Col>
        }
      </PageContainer>
}
